import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { App, Button, Col, Form, Input, Modal, Row, Select, Space, Spin, Tooltip } from 'antd';

import * as sharedActions from '../../../shared/store/actions';
import * as actions from '../store/actions';

import AssignAgent from './assign-agent-modal';

import { UserTypes } from '../../../shared/constants/enums';
import { renderNoDataOrSpinner } from '../../../shared/utils';

import { isArray, isEmpty } from 'lodash';
import { AppDispatch, RootState } from '../../../store';
import { SaveTwilioAccountSettingsRequest } from '../models';
import { Store } from 'antd/es/form/interface';

export type TwilioSettingsProps = {
  showModal: boolean;
  onCloseModal: () => void;
  resetAndRefresh: () => void;
};

const TwilioSettings = ({ showModal, onCloseModal, resetAndRefresh }: TwilioSettingsProps) => {
  const dispatch = useDispatch<AppDispatch>();

  const { modal } = App.useApp();
  const didMount = useRef(false);

  const [formRef] = Form.useForm<SaveTwilioAccountSettingsRequest>();

  const [isModified, setIsModified] = useState<boolean>(false);
  const [sidForGetAllTwilioPhones, setSidForGetAllTwilioPhones] = useState<string>();
  const [authTokenForGetAllTwilioPhones, setAuthTokenForGetAllTwilioPhones] = useState<string>();
  const [initiatingDone, setInitiatingDone] = useState<boolean>(false);
  const [showAssignAgent, setShowAssignAgent] = useState<boolean>(false);

  const { twilioAccountSettings, twilioAccountSettingsLoading, saveTwilioAccountSettingsLoading, allTwilioPhones, allTwilioPhonesLoading } =
    useSelector((state: RootState) => state.twilio);

  const { messageControlParametersLoading } = useSelector((state: RootState) => state.smses);

  const { smsSettingLoading } = useSelector((state: RootState) => state.smsSetting);

  const { userInfo } = useSelector((state: RootState) => state.shared);

  useEffect(() => {
    if (!didMount.current) {
      dispatch(sharedActions.getUserInfo());

      dispatch(actions.getSmsSetting());

      dispatch(actions.getTwilioAccountSettings()).then(() => {
        setInitiatingDone(true);
        onReset();
      });

      didMount.current = true;
    }
  }, []);

  useEffect(() => {
    onReset();
  }, [twilioAccountSettings, twilioAccountSettingsLoading]);

  const onClose = (isModifiedModal: boolean) => {
    if (isModifiedModal) {
      modal.confirm({
        title: 'All changes will be lost. Are you sure you want to close?',
        okText: 'Yes',
        cancelText: 'No',
        onOk: () => {
          onReset();
          onCloseModal?.();
        },
      });
    } else {
      onReset();
      resetAndRefresh?.();
      onCloseModal?.();
    }
  };

  const onFinish = () => {
    formRef.validateFields().then((formData) => {
      dispatch(actions.saveTwilioAccountSettings(formData)).then(() => {
        dispatch(actions.getMessageControlParameters()).then(() => {
          modal.success({
            title: 'Your settings are saved successfully',
            onOk: () => {
              setIsModified(false);
              onClose(false);
            },
          });
        });
      });
    });
  };

  const onRemove = () => {
    if (!twilioAccountSettings) {
      return;
    }

    modal.confirm({
      title: 'This is remove the current Twilio Account settings. Are you sure you want to continue?',
      okText: 'Yes',
      cancelText: 'No',
      onOk: () => {
        return new Promise((resolve, reject) => {
          dispatch(
            actions.removeTwilioAccount({
              id: twilioAccountSettings.id,
            }),
          )
            .then(() => {
              return dispatch(actions.getMessageControlParameters()).then(() => {
                modal.success({
                  title: 'Your settings are saved successfully',
                  onOk: () => {
                    setIsModified(false);
                    onClose(false);
                    resolve(undefined);
                  },
                });
              });
            })
            .catch(reject);
        });
      },
    });
  };

  const onReset = () => {
    formRef.resetFields();
    dispatch(actions.setAllTwilioPhones([]));
    setIsModified(false);
  };

  const onPhonesSelectionOpen = (open: boolean) => {
    if (open === true) {
      const accountSid = formRef.getFieldValue('accountSid');
      const authToken = formRef.getFieldValue('authToken');

      if (!isEmpty(accountSid) && !isEmpty(authToken) && !(sidForGetAllTwilioPhones === accountSid && authTokenForGetAllTwilioPhones === authToken)) {
        setSidForGetAllTwilioPhones(accountSid);
        setAuthTokenForGetAllTwilioPhones(authToken);
        dispatch(actions.getAllTwilioPhones({ accountSid, authToken }));
      }
    }
  };

  const resetPhoneSelectionData = () => {
    formRef.setFieldsValue({
      incomingPhoneNumber: undefined,
    });
    dispatch(actions.setAllTwilioPhones([]));
  };

  const onAssignAgent = () => {
    if (isEmpty(twilioAccountSettings?.id) || isModified) {
      return modal.warning({
        title: 'Current settings are not saved. Please save and try again.',
      });
    }

    setShowAssignAgent(true);
  };

  const onAssignAgentClose = () => {
    dispatch(actions.getMessageControlParameters()).then(() => {
      modal.success({
        title: 'Your settings are saved successfully',
        onOk: () => {
          setShowAssignAgent(false);
          setIsModified(false);
          onClose(false);
        },
      });
    });
  };

  return (
    <Modal
      title="Twilio account settings"
      open={showModal}
      footer={null}
      onCancel={() => onClose(isModified)}
      onClose={() => onClose(isModified)}
      width={600}
    >
      <div className="account-modal-settings">
        <Spin spinning={twilioAccountSettingsLoading || !initiatingDone}>
          <Form
            form={formRef}
            size="small"
            className="twilio-account-form"
            labelCol={{ span: 8 }}
            wrapperCol={{ span: 16 }}
            initialValues={twilioAccountSettings as Store}
            onFinish={onFinish}
            onChange={() => (isModified ? null : setIsModified(true))}
          >
            <Form.Item hidden name="id">
              <Input type="hidden" />
            </Form.Item>

            <Form.Item label="Sid" name="accountSid" rules={[{ type: 'string', required: true, max: 128 }]}>
              <Input onChange={resetPhoneSelectionData} />
            </Form.Item>

            <Form.Item label="AuthToken" name="authToken" rules={[{ type: 'string', required: true, max: 128 }]}>
              <Input onChange={resetPhoneSelectionData} />
            </Form.Item>

            <Form.Item label="Phones" name="incomingPhoneNumber" rules={[{ type: 'string', required: true, max: 128 }]}>
              <Select
                placeholder="Select a phone"
                notFoundContent={renderNoDataOrSpinner(allTwilioPhonesLoading)}
                onDropdownVisibleChange={onPhonesSelectionOpen}
              >
                {isArray(allTwilioPhones)
                  ? allTwilioPhones.map((phone) => (
                      <Select.Option key={phone} value={phone}>
                        {phone}
                      </Select.Option>
                    ))
                  : null}
              </Select>
            </Form.Item>

            <Row>
              <Col span={24} className="modal-form-control-buttons">
                <Space className="control-buttons-space">
                  <Button type="primary" htmlType="submit" loading={saveTwilioAccountSettingsLoading || messageControlParametersLoading}>
                    {!isEmpty(twilioAccountSettings?.id) ? 'Update' : 'Save'}
                  </Button>

                  {userInfo?.userType === UserTypes.InsuranceAgency && !isEmpty(twilioAccountSettings?.id) ? (
                    <Button
                      loading={saveTwilioAccountSettingsLoading || messageControlParametersLoading}
                      onClick={onAssignAgent}
                      disabled={smsSettingLoading}
                    >
                      Assign phone
                    </Button>
                  ) : undefined}

                  <Tooltip title="Reset all changes to the unchanged state">
                    <Button htmlType="button" onClick={onReset}>
                      Reset
                    </Button>
                  </Tooltip>

                  {!isEmpty(twilioAccountSettings?.id) ? (
                    <Button type="primary" danger loading={saveTwilioAccountSettingsLoading || messageControlParametersLoading} onClick={onRemove}>
                      Remove
                    </Button>
                  ) : undefined}

                  <Button onClick={() => onClose(isModified)}>Cancel</Button>
                </Space>
              </Col>
            </Row>
          </Form>
        </Spin>

        {showAssignAgent ? (
          <AssignAgent
            open={showAssignAgent}
            onAssignAgentClose={onAssignAgentClose}
            accountSid={twilioAccountSettings?.accountSid as string}
            authToken={twilioAccountSettings?.authToken as string}
          />
        ) : null}
      </div>
    </Modal>
  );
};

export default TwilioSettings;
